import { useState, useEffect } from 'react';
import { toast } from '../../utils/toast';
import { AlertTriangle, XCircle, Loader2, Clock, User } from 'lucide-react';
import { api, type ActiveSupportSession } from '../../api/client';
import { confirmDialog } from '../../utils/confirmDialog';
import { parseUTC } from '../../utils/dateUtils';

export function ActiveSupportSessionBanner() {
  const [activeSessions, setActiveSessions] = useState<ActiveSupportSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [terminatingSession, setTerminatingSession] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadSessions = async () => {
    try {
      const response = await api.getActiveSupportSessions();
      setActiveSessions(response.sessions);
    } catch (error) {
      // Silently fail - user might not have permission or API might not be configured
      setActiveSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    if (!await confirmDialog('Möchten Sie diese Support-Session wirklich beenden? Der Administrator wird sofort ausgeloggt.')) {
      return;
    }

    try {
      setTerminatingSession(sessionId);
      await api.terminateSupportSession(sessionId);
      await loadSessions();
    } catch (error) {
      console.error('Failed to terminate session:', error);
      toast('Fehler beim Beenden der Session. Bitte versuchen Sie es erneut.');
    } finally {
      setTerminatingSession(null);
    }
  };

  const getTimeRemaining = (expiresAt: string) => {
    const remaining = parseUTC(expiresAt).getTime() - Date.now();
    if (remaining <= 0) return 'Abgelaufen';

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  // Don't show anything if loading or no active sessions
  if (isLoading || activeSessions.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="flex-shrink-0 animate-pulse" size={24} />
              <div>
                <p className="font-semibold">
                  {activeSessions.length === 1
                    ? 'Support-Administrator ist eingeloggt'
                    : `${activeSessions.length} Support-Administratoren sind eingeloggt`}
                </p>
                {!isExpanded && activeSessions.length === 1 && (
                  <p className="text-sm text-red-200">
                    {activeSessions[0].superAdmin.name} - Verbleibend: {getTimeRemaining(activeSessions[0].expiresAt)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="px-3 py-1 text-sm bg-red-500 hover:bg-red-400 rounded-lg transition-colors touch-manipulation"
              >
                {isExpanded ? 'Minimieren' : 'Details'}
              </button>
              {activeSessions.length === 1 && (
                <button
                  onClick={() => handleTerminateSession(activeSessions[0].id)}
                  disabled={terminatingSession === activeSessions[0].id}
                  className="flex items-center gap-2 px-3 py-1 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors touch-manipulation disabled:opacity-50"
                >
                  {terminatingSession === activeSessions[0].id ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <XCircle size={16} />
                  )}
                  Session beenden
                </button>
              )}
            </div>
          </div>

          {/* Expanded view with all sessions */}
          {isExpanded && (
            <div className="mt-4 space-y-3">
              {activeSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between bg-red-500/30 rounded-lg p-3"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <User size={20} />
                    </div>
                    <div>
                      <p className="font-medium">{session.superAdmin.name}</p>
                      <p className="text-sm text-red-200">{session.superAdmin.email}</p>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-red-200 ml-4">
                      <Clock size={14} />
                      <span>Verbleibend: {getTimeRemaining(session.expiresAt)}</span>
                    </div>
                  </div>
                  {activeSessions.length > 1 && (
                    <button
                      onClick={() => handleTerminateSession(session.id)}
                      disabled={terminatingSession === session.id}
                      className="flex items-center gap-2 px-3 py-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors touch-manipulation disabled:opacity-50"
                    >
                      {terminatingSession === session.id ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <XCircle size={16} />
                      )}
                      Beenden
                    </button>
                  )}
                </div>
              ))}

              <p className="text-sm text-red-200 mt-2">
                Der Administrator kann Ihre Daten einsehen und Aktionen in Ihrem Namen durchführen.
                Sie können die Session jederzeit beenden.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
